import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react'

import { type Edge, useNodesInitialized, useReactFlow } from '@xyflow/react'

import { SIZES } from './types'
import { buildParentMap, expandAncestorsAndSelectNode } from './treeUtils'
import { type CustomNode } from './types'

const SIDEPANEL_WIDTH = 600
const SIDEPANEL_VIEWPORT_OFFSET_RATIO = 0.35
const SELECTION_VIEWPORT_VERTICAL_OFFSET = 120

interface UseNodeSelectionFromRouteProps {
  nodeId?: string
  setNodes: Dispatch<SetStateAction<CustomNode[]>>
  edges: Edge[]
}

const useNodeSelectionFromRoute = ({ nodeId, setNodes, edges }: UseNodeSelectionFromRouteProps) => {
  const { getNode, getZoom, setCenter, viewportInitialized } = useReactFlow()
  const nodesInitialized = useNodesInitialized()
  const [pendingNodeId, setPendingNodeId] = useState<string>()

  // Build parent map from original edges
  const parentMapRef = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    parentMapRef.current = buildParentMap(edges)
  }, [edges])

  useEffect(() => {
    if (nodeId) {
      let targetNodeId: string | undefined

      setNodes((nds) => {
        const nodeToSelect = nds.find((n) => n.id === nodeId || n.data.node_uuid === nodeId)
        if (!nodeToSelect) {
          return nds
        }

        targetNodeId = nodeToSelect.id

        return expandAncestorsAndSelectNode(nds, nodeToSelect.id, parentMapRef.current)
      })

      setPendingNodeId(targetNodeId)
    } else {
      setPendingNodeId(undefined)

      // Deselect all nodes when there's no id param
      setNodes((nds) => {
        const hasSelectedNodes = nds.some((n) => n.selected)
        if (!hasSelectedNodes) {
          return nds
        }

        return nds.map((n) => (n.selected ? { ...n, selected: false } : n))
      })
    }
  }, [nodeId, setNodes])

  useEffect(() => {
    if (!pendingNodeId || !viewportInitialized || !nodesInitialized) {
      return
    }

    const selectedNode = getNode(pendingNodeId)
    if (!selectedNode) {
      setPendingNodeId(undefined)
      return
    }

    const zoom = Math.min(getZoom() || 1, 1)
    const nodeCenterX = selectedNode.position.x + SIZES.NODE_WIDTH / 2
    const nodeCenterY = selectedNode.position.y + SIZES.NODE_HEIGHT / 2
    const flowOffsetX = (SIDEPANEL_WIDTH * SIDEPANEL_VIEWPORT_OFFSET_RATIO) / zoom
    const flowOffsetY = SELECTION_VIEWPORT_VERTICAL_OFFSET / zoom

    void setCenter(nodeCenterX + flowOffsetX, nodeCenterY + flowOffsetY, {
      duration: 300,
      zoom,
    })

    setPendingNodeId(undefined)
  }, [getNode, getZoom, nodesInitialized, pendingNodeId, setCenter, viewportInitialized])
}

export default useNodeSelectionFromRoute
